import {
  JourneyPlannerState,
  Leg,
  Route,
  RouteDestination,
  RouteDestinationLoadAction,
  RouteDestinationLoadService,
  JourneyWaypoint,
  LoadMetadata,
  WaypointLocationInfo,
  DestinationActionTypeId,
  createInitialPlannerState,
  EditedLoadActionConfig,
} from '../plannerTypes'

export type PlannerAction =
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'SET_SAVING'; isSaving: boolean }
  | { type: 'SET_ERROR'; error?: string }
  | { type: 'SET_DIRTY'; isDirty: boolean }
  | { type: 'RESET_STATE'; tenantId: number }
  | { type: 'LOAD_JOURNEY'; journey: Partial<JourneyPlannerState> }
  | { type: 'SET_LOAD_METADATA'; loadMetadataById: Record<number, LoadMetadata> }
  | { type: 'SET_LEG_TRANSPORT_MODE'; legIndex: number; transportModeId: number }
  | {
      type: 'ADD_LEG'
      transportModeId?: number
      selectedLoadIds?: number[]
      waypointLocations?: WaypointLocationInfo[]
    }
  | { type: 'REMOVE_LEG'; legIndex: number }
  | { type: 'ADD_ROUTE_TO_LEG'; legIndex: number }
  | { type: 'REMOVE_ROUTE'; legIndex: number; routeIndex: number }
  | {
      type: 'UPDATE_ROUTE_CARRIER'
      legIndex: number
      routeIndex: number
      carrierId?: number
      carrierName?: string
    }
  | {
      type: 'UPDATE_ROUTE_TRANSIT_IDENTIFIER'
      legIndex: number
      routeIndex: number
      transitIdentifier?: string
    }
  | {
      type: 'UPDATE_ROUTE_SCHEDULED_DEPARTURE'
      legIndex: number
      routeIndex: number
      scheduledDepartureDate?: string
    }
  | {
      type: 'UPDATE_ROUTE_SCHEDULED_ARRIVAL'
      legIndex: number
      routeIndex: number
      scheduledArrivalDate?: string
    }
  | {
      type: 'UPDATE_DESTINATION_DATE'
      legIndex: number
      routeIndex: number
      destIndex: number
      scheduledDate: string
    }
  | {
      type: 'UPDATE_DESTINATION_LOCATION'
      legIndex: number
      routeIndex: number
      destIndex: number
      locationId: number
      locationName?: string
    }
  | {
      type: 'ADD_LOAD_ACTION_TO_DESTINATION'
      legIndex: number
      routeIndex: number
      destIndex: number
      loadId: number
      destinationActionTypeId: number
    }
  | {
      type: 'REMOVE_LOAD_ACTION_FROM_DESTINATION'
      legIndex: number
      routeIndex: number
      destIndex: number
      loadActionIndex: number
    }
  | {
      type: 'ADD_DESTINATION_TO_ROUTE'
      legIndex: number
      routeIndex: number
      locationId: number
      locationName?: string
      scheduledDate: string
    }
  | {
      type: 'REMOVE_DESTINATION_FROM_ROUTE'
      legIndex: number
      routeIndex: number
      destIndex: number
    }
  | {
      type: 'ADD_WAYPOINT'
      afterLegIndex: number
      locationId?: number
      locationName?: string
      address?: string
    }
  | {
      type: 'REMOVE_WAYPOINT'
      waypointIndex: number
    }
  | {
      type: 'UPDATE_WAYPOINT_LOCATION'
      waypointIndex: number
      locationId: number
      locationName?: string
      address?: string
    }
  | {
      type: 'ADD_WAYPOINT_WITH_LEG'
      position: 'before' | 'after'
      legIndex: number
      locationId: number
      locationName?: string
      address?: string
      transportModeId: number
    }
  | {
      type: 'DELETE_WAYPOINT_WITH_LEG'
      waypointIndex: number
      legToRemove: 'before' | 'after'
    }
  | {
      type: 'SPLIT_ROUTE_ONE_PER_CONSIGNMENT'
      legIndex: number
      routeIndex: number
      selectedLoadIds: number[]
      carrierId?: number
      carrierName?: string
      transitIdentifier?: string
      scheduledDepartureDate?: string
      scheduledArrivalDate?: string
    }
  | {
      type: 'SPLIT_ROUTE_ALL_CONSIGNMENTS'
      legIndex: number
      routeIndex: number
      selectedLoadIds: number[]
      carrierId?: number
      carrierName?: string
      transitIdentifier?: string
      scheduledDepartureDate?: string
      scheduledArrivalDate?: string
    }
  | {
      type: 'MERGE_ROUTES'
      legIndex: number
      selectedRouteIndices: number[]
      carrierId?: number
      carrierName?: string
      transitIdentifier?: string
      scheduledDepartureDate?: string
      scheduledArrivalDate?: string
    }
  | {
      type: 'INSERT_DESTINATION_WITH_ACTIONS'
      legIndex: number
      routeIndex: number
      insertAfterDestIndex: number
      locationId: number
      locationName?: string
      address?: string
      scheduledDate: string
      loadActions: {
        loadId: number
        destinationActionTypeId: number
      }[]
      loadServices: {
        loadId: number
        serviceId: number
      }[]
    }
  | {
      type: 'UPDATE_LOAD_ACTIONS_AT_DESTINATION'
      legIndex: number
      routeIndex: number
      destIndex: number
      updatedActions: EditedLoadActionConfig[]
      removedLoadIds: number[]
    }

let nextTempId = -1
const getNextTempId = () => nextTempId--

const createDestinationWithLoadActions = (
  routeId: number,
  sequence: number,
  isBoundary: boolean,
  loadIds: number[],
  actionTypeId: number,
  locationId?: number,
  locationName?: string,
  address?: string
): RouteDestination => {
  const destId = getNextTempId()
  const loadActions: RouteDestinationLoadAction[] = loadIds.map((loadId) => ({
    id: getNextTempId(),
    routeDestinationId: destId,
    loadId,
    destinationActionTypeId: actionTypeId,
  }))
  return {
    id: destId,
    routeId,
    locationId: locationId || 0,
    locationName,
    address,
    scheduledDate: new Date().toISOString(),
    sequence,
    isUserCreated: true,
    isBoundary,
    loadActions: loadActions,
    loadServices: [],
  }
}

const createEmptyDestination = (
  routeId: number,
  sequence: number,
  isBoundary = false
): RouteDestination => ({
  id: getNextTempId(),
  routeId,
  locationId: 0,
  scheduledDate: new Date().toISOString(),
  sequence,
  isUserCreated: true,
  isBoundary,
  loadActions: [],
  loadServices: [],
})

const createRouteWithLoadActions = (
  legId: number,
  displayOrder: number,
  selectedLoadIds: number[],
  pickupLocationId?: number,
  pickupLocationName?: string,
  pickupAddress?: string,
  dropoffLocationId?: number,
  dropoffLocationName?: string,
  dropoffAddress?: string
): Route => {
  const routeId = getNextTempId()
  return {
    id: routeId,
    legId,
    displayOrder,
    destinations: [
      createDestinationWithLoadActions(
        routeId,
        1,
        true,
        selectedLoadIds,
        DestinationActionTypeId.Pickup,
        pickupLocationId,
        pickupLocationName,
        pickupAddress
      ),
      createDestinationWithLoadActions(
        routeId,
        2,
        true,
        selectedLoadIds,
        DestinationActionTypeId.DropOff,
        dropoffLocationId,
        dropoffLocationName,
        dropoffAddress
      ),
    ],
    loadIds: [...selectedLoadIds],
  }
}

const createEmptyRoute = (legId: number, displayOrder: number): Route => {
  const routeId = getNextTempId()
  return {
    id: routeId,
    legId,
    displayOrder,
    destinations: [
      createEmptyDestination(routeId, 1, true),
      createEmptyDestination(routeId, 2, true),
    ],
    loadIds: [],
  }
}

const createLegWithLoadActions = (
  journeyId: number,
  sequence: number,
  transportModeId: number | undefined,
  selectedLoadIds: number[],
  waypointLocations: WaypointLocationInfo[]
): Leg => {
  const legId = getNextTempId()
  // Use first waypoint as pickup location and last waypoint as dropoff location
  const pickupLocationId = waypointLocations.length > 0 ? waypointLocations[0].locationId : undefined
  const pickupLocationName = waypointLocations.length > 0 ? waypointLocations[0].locationName : undefined
  const pickupAddress = waypointLocations.length > 0 ? waypointLocations[0].address : undefined
  const dropoffLocationId =
    waypointLocations.length > 1 ? waypointLocations[waypointLocations.length - 1].locationId : undefined
  const dropoffLocationName =
    waypointLocations.length > 1 ? waypointLocations[waypointLocations.length - 1].locationName : undefined
  const dropoffAddress =
    waypointLocations.length > 1 ? waypointLocations[waypointLocations.length - 1].address : undefined
  return {
    id: legId,
    journeyId: journeyId || 0,
    sequence,
    transportModeId,
    fulfilmentRequired: true,
    routes: [
      createRouteWithLoadActions(
        legId,
        1,
        selectedLoadIds,
        pickupLocationId,
        pickupLocationName,
        pickupAddress,
        dropoffLocationId,
        dropoffLocationName,
        dropoffAddress
      ),
    ],
  }
}

const createEmptyLeg = (journeyId: number, sequence: number): Leg => {
  const legId = getNextTempId()
  return {
    id: legId,
    journeyId: journeyId || 0,
    sequence,
    fulfilmentRequired: true,
    routes: [createEmptyRoute(legId, 1)],
  }
}

export const journeyPlannerReducer = (
  state: JourneyPlannerState,
  action: PlannerAction
): JourneyPlannerState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.isLoading }

    case 'SET_SAVING':
      return { ...state, isSaving: action.isSaving }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_DIRTY':
      return { ...state, isDirty: action.isDirty }

    case 'RESET_STATE':
      return createInitialPlannerState(action.tenantId)

    case 'LOAD_JOURNEY':
      return {
        ...state,
        ...action.journey,
        isDirty: false,
        isLoading: false,
      }

    case 'SET_LOAD_METADATA':
      return {
        ...state,
        loadMetadataById: action.loadMetadataById,
      }

    case 'SET_LEG_TRANSPORT_MODE': {
      const newLegs = [...state.legs]
      if (newLegs[action.legIndex]) {
        newLegs[action.legIndex] = {
          ...newLegs[action.legIndex],
          transportModeId: action.transportModeId,
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'ADD_LEG': {
      const newSequence = state.legs.length + 1
      const selectedLoadIds = action.selectedLoadIds || []
      const waypointLocations = action.waypointLocations || []
      const newLeg =
        selectedLoadIds.length > 0
          ? createLegWithLoadActions(
              state.id || 0,
              newSequence,
              action.transportModeId,
              selectedLoadIds,
              waypointLocations
            )
          : createEmptyLeg(state.id || 0, newSequence)
      return {
        ...state,
        legs: [...state.legs, newLeg],
        isDirty: true,
      }
    }

    case 'REMOVE_LEG': {
      const newLegs = state.legs.filter((_, index) => index !== action.legIndex)
      const resequencedLegs = newLegs.map((leg, index) => ({
        ...leg,
        sequence: index + 1,
      }))
      return { ...state, legs: resequencedLegs, isDirty: true }
    }

    case 'ADD_ROUTE_TO_LEG': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg) {
        const newDisplayOrder = leg.routes.length + 1
        const newRoute = createEmptyRoute(leg.id, newDisplayOrder)
        newLegs[action.legIndex] = {
          ...leg,
          routes: [...leg.routes, newRoute],
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'REMOVE_ROUTE': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes.length > 1) {
        const newRoutes = leg.routes.filter((_, index) => index !== action.routeIndex)
        const resequencedRoutes = newRoutes.map((route, index) => ({
          ...route,
          displayOrder: index + 1,
        }))
        newLegs[action.legIndex] = {
          ...leg,
          routes: resequencedRoutes,
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'UPDATE_ROUTE_CARRIER': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        newRoutes[action.routeIndex] = {
          ...newRoutes[action.routeIndex],
          carrierId: action.carrierId,
          carrierName: action.carrierName,
        }
        newLegs[action.legIndex] = { ...leg, routes: newRoutes }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'UPDATE_ROUTE_TRANSIT_IDENTIFIER': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        newRoutes[action.routeIndex] = {
          ...newRoutes[action.routeIndex],
          transitIdentifier: action.transitIdentifier,
        }
        newLegs[action.legIndex] = { ...leg, routes: newRoutes }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'UPDATE_ROUTE_SCHEDULED_DEPARTURE': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        newRoutes[action.routeIndex] = {
          ...newRoutes[action.routeIndex],
          scheduledDepartureDate: action.scheduledDepartureDate,
        }
        newLegs[action.legIndex] = { ...leg, routes: newRoutes }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'UPDATE_ROUTE_SCHEDULED_ARRIVAL': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        newRoutes[action.routeIndex] = {
          ...newRoutes[action.routeIndex],
          scheduledArrivalDate: action.scheduledArrivalDate,
        }
        newLegs[action.legIndex] = { ...leg, routes: newRoutes }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'UPDATE_DESTINATION_DATE': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        const route = newRoutes[action.routeIndex]
        if (route.destinations[action.destIndex]) {
          const newDestinations = [...route.destinations]
          newDestinations[action.destIndex] = {
            ...newDestinations[action.destIndex],
            scheduledDate: action.scheduledDate,
          }
          newRoutes[action.routeIndex] = { ...route, destinations: newDestinations }
          newLegs[action.legIndex] = { ...leg, routes: newRoutes }
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'UPDATE_DESTINATION_LOCATION': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        const route = newRoutes[action.routeIndex]
        if (route.destinations[action.destIndex]) {
          const newDestinations = [...route.destinations]
          newDestinations[action.destIndex] = {
            ...newDestinations[action.destIndex],
            locationId: action.locationId,
            locationName: action.locationName,
          }
          newRoutes[action.routeIndex] = { ...route, destinations: newDestinations }
          newLegs[action.legIndex] = { ...leg, routes: newRoutes }
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'ADD_LOAD_ACTION_TO_DESTINATION': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        const route = newRoutes[action.routeIndex]
        if (route.destinations[action.destIndex]) {
          const newDestinations = [...route.destinations]
          const dest = newDestinations[action.destIndex]
          const newAction: RouteDestinationLoadAction = {
            id: getNextTempId(),
            routeDestinationId: dest.id,
            loadId: action.loadId,
            destinationActionTypeId: action.destinationActionTypeId,
          }
          newDestinations[action.destIndex] = {
            ...dest,
            loadActions: [...dest.loadActions, newAction],
          }
          newRoutes[action.routeIndex] = { ...route, destinations: newDestinations }
          newLegs[action.legIndex] = { ...leg, routes: newRoutes }
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'REMOVE_LOAD_ACTION_FROM_DESTINATION': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        const route = newRoutes[action.routeIndex]
        if (route.destinations[action.destIndex]) {
          const newDestinations = [...route.destinations]
          const dest = newDestinations[action.destIndex]
          newDestinations[action.destIndex] = {
            ...dest,
                        loadActions: dest.loadActions.filter(
                          (_, index) => index !== action.loadActionIndex
                        ),
          }
          newRoutes[action.routeIndex] = { ...route, destinations: newDestinations }
          newLegs[action.legIndex] = { ...leg, routes: newRoutes }
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'ADD_DESTINATION_TO_ROUTE': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        const route = newRoutes[action.routeIndex]
        const newSequence = route.destinations.length + 1
        const newDest: RouteDestination = {
          id: getNextTempId(),
          routeId: route.id,
          locationId: action.locationId,
          locationName: action.locationName,
          scheduledDate: action.scheduledDate,
          sequence: newSequence,
          isUserCreated: true,
          isBoundary: false,
          loadActions: [],
          loadServices: [],
        }
        newRoutes[action.routeIndex] = {
          ...route,
          destinations: [...route.destinations, newDest],
        }
        newLegs[action.legIndex] = { ...leg, routes: newRoutes }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'REMOVE_DESTINATION_FROM_ROUTE': {
      const newLegs = [...state.legs]
      const leg = newLegs[action.legIndex]
      if (leg && leg.routes[action.routeIndex]) {
        const newRoutes = [...leg.routes]
        const route = newRoutes[action.routeIndex]
        if (route.destinations.length > 2) {
          const newDestinations = route.destinations.filter(
            (_, index) => index !== action.destIndex
          )
          const resequencedDestinations = newDestinations.map((dest, index) => ({
            ...dest,
            sequence: index + 1,
          }))
          newRoutes[action.routeIndex] = { ...route, destinations: resequencedDestinations }
          newLegs[action.legIndex] = { ...leg, routes: newRoutes }
        }
      }
      return { ...state, legs: newLegs, isDirty: true }
    }

    case 'ADD_WAYPOINT': {
      const newWaypoint: JourneyWaypoint = {
        id: getNextTempId(),
        journeyId: state.id || 0,
        locationId: action.locationId || 0,
        locationRevision: 0,
        sequence: state.waypoints.length + 1,
        isUserCreated: true,
        isOrigin: false,
        isFinal: false,
        locationName: action.locationName,
        address: action.address,
        afterLegIndex: action.afterLegIndex,
      }
      const newWaypoints = [...state.waypoints, newWaypoint]
      const resequencedWaypoints = newWaypoints.map((wp, index) => ({
        ...wp,
        sequence: index + 1,
      }))
      return { ...state, waypoints: resequencedWaypoints, isDirty: true }
    }

    case 'REMOVE_WAYPOINT': {
      const newWaypoints = state.waypoints.filter((_, index) => index !== action.waypointIndex)
      const resequencedWaypoints = newWaypoints.map((wp, index) => ({
        ...wp,
        sequence: index + 1,
      }))
      return { ...state, waypoints: resequencedWaypoints, isDirty: true }
    }

    case 'UPDATE_WAYPOINT_LOCATION': {
      const newWaypoints = [...state.waypoints]
      if (newWaypoints[action.waypointIndex]) {
        newWaypoints[action.waypointIndex] = {
          ...newWaypoints[action.waypointIndex],
          locationId: action.locationId,
          locationName: action.locationName,
          address: action.address,
        }
      }
      return { ...state, waypoints: newWaypoints, isDirty: true }
    }

    case 'ADD_WAYPOINT_WITH_LEG': {
      const { position, legIndex, locationId, locationName, address, transportModeId } = action

      // Get the reference leg that will be modified
      const referenceLeg = state.legs[legIndex]
      if (!referenceLeg) {
        return state
      }

      // Create the new waypoint
      // afterLegIndex is the index of the leg that the waypoint comes after
      // For 'after': waypoint comes after the reference leg (at legIndex)
      // For 'before': waypoint comes after the new leg (which is inserted at legIndex)
      const newWaypoint: JourneyWaypoint = {
        id: getNextTempId(),
        journeyId: state.id || 0,
        locationId,
        locationRevision: 0,
        sequence: 0, // Will be resequenced
        isUserCreated: true,
        isOrigin: false,
        isFinal: false,
        locationName,
        address,
        afterLegIndex: legIndex,
      }

      // Create the new leg with routes that mirror the reference leg's routes
      const newLegId = getNextTempId()

      let updatedReferenceLeg: Leg
      let newLeg: Leg

      if (position === 'after') {
        // "Add Waypoint After" means:
        // - The existing leg at legIndex should END at the new waypoint location
        // - The new leg should START at the new waypoint location and END at the original destination
        
        // Update the reference leg: change the last destination of each route to the new waypoint location
        // The load actions at the last destination stay with the new leg (they move with the original location)
        updatedReferenceLeg = {
          ...referenceLeg,
          routes: referenceLeg.routes.map((route) => {
            const lastDestIndex = route.destinations.length - 1
            const lastDest = route.destinations[lastDestIndex]
            
            // Get load IDs from the original last destination's drop-off actions
            const loadIdsInRoute = lastDest.loadActions
              .filter((a) => a.destinationActionTypeId === DestinationActionTypeId.DropOff)
              .map((a) => a.loadId)
            
            const newLastDestId = getNextTempId()
            // Create a new last destination at the new waypoint location with Drop-off actions (Bug fix: auto-generate Drop-off on last destination)
            const newLastDest: RouteDestination = {
              id: newLastDestId,
              routeId: route.id,
              locationId,
              locationName,
              address,
              scheduledDate: lastDest.scheduledDate,
              sequence: lastDest.sequence,
              isUserCreated: true,
              isBoundary: true,
              loadActions: loadIdsInRoute.map((loadId) => ({
                id: getNextTempId(),
                routeDestinationId: newLastDestId,
                loadId,
                destinationActionTypeId: DestinationActionTypeId.DropOff,
              })),
              loadServices: [],
            }
            
            return {
              ...route,
              destinations: [
                ...route.destinations.slice(0, lastDestIndex),
                newLastDest,
              ],
            }
          }),
        }

        // Create the new leg with a SINGLE route that consolidates all loads from all routes
        // Collect all unique load IDs from all routes' last destinations (drop-off actions)
        const allLoadIds = new Set<number>()
        const allDropoffActions: RouteDestinationLoadAction[] = []
        const allDropoffServices: RouteDestinationLoadService[] = []
        let lastDestForSchedule = referenceLeg.routes[0]?.destinations[referenceLeg.routes[0]?.destinations.length - 1]
        let lastDestLocation = {
          locationId: lastDestForSchedule?.locationId || 0,
          locationName: lastDestForSchedule?.locationName,
          address: lastDestForSchedule?.address,
        }
        
        referenceLeg.routes.forEach((route) => {
          const lastDest = route.destinations[route.destinations.length - 1]
          if (lastDest) {
            lastDest.loadActions
              .filter((a) => a.destinationActionTypeId === DestinationActionTypeId.DropOff)
              .forEach((a) => {
                if (!allLoadIds.has(a.loadId)) {
                  allLoadIds.add(a.loadId)
                  allDropoffActions.push(a)
                }
              })
            if (lastDest.loadServices) {
              allDropoffServices.push(...lastDest.loadServices)
            }
            // Use the last route's last destination for location info
            lastDestLocation = {
              locationId: lastDest.locationId,
              locationName: lastDest.locationName,
              address: lastDest.address,
            }
            lastDestForSchedule = lastDest
          }
        })
        
        const newRouteId = getNextTempId()
        const pickupDestId = getNextTempId()
        const dropoffDestId = getNextTempId()
        
        // Pickup at new waypoint location - needs Pickup actions for all loads being transported
        const pickupDest: RouteDestination = {
          id: pickupDestId,
          routeId: newRouteId,
          locationId,
          locationName,
          address,
          scheduledDate: lastDestForSchedule?.scheduledDate || new Date().toISOString(),
          sequence: 1,
          isUserCreated: true,
          isBoundary: true,
          loadActions: Array.from(allLoadIds).map((loadId) => ({
            id: getNextTempId(),
            routeDestinationId: pickupDestId,
            loadId,
            destinationActionTypeId: DestinationActionTypeId.Pickup,
          })),
          loadServices: [],
        }
        
        // Dropoff at original destination location (with all the original load actions)
        const dropoffDest: RouteDestination = {
          id: dropoffDestId,
          routeId: newRouteId,
          locationId: lastDestLocation.locationId,
          locationName: lastDestLocation.locationName,
          address: lastDestLocation.address,
          scheduledDate: lastDestForSchedule?.scheduledDate || new Date().toISOString(),
          sequence: 2,
          isUserCreated: true,
          isBoundary: true,
          loadActions: Array.from(allLoadIds).map((loadId) => ({
            id: getNextTempId(),
            routeDestinationId: dropoffDestId,
            loadId,
            destinationActionTypeId: DestinationActionTypeId.DropOff,
          })),
          loadServices: allDropoffServices,
        }
        
        newLeg = {
          id: newLegId,
          journeyId: state.id || 0,
          sequence: 0, // Will be resequenced
          transportModeId,
          fulfilmentRequired: true,
          routes: [
            {
              id: newRouteId,
              legId: newLegId,
              displayOrder: 1,
              destinations: [pickupDest, dropoffDest],
              loadIds: Array.from(allLoadIds),
            },
          ],
        }
      } else {
        // "Add Waypoint Before" means:
        // - The existing leg at legIndex should START at the new waypoint location
        // - The new leg should START at the original origin and END at the new waypoint location
        
        // Update the reference leg: change the first destination of each route to the new waypoint location
        // The load actions at the first destination stay with the new leg (they move with the original location)
        updatedReferenceLeg = {
          ...referenceLeg,
          routes: referenceLeg.routes.map((route) => {
            const firstDest = route.destinations[0]
            
            // Get load IDs from the original first destination's pickup actions
            const loadIdsInRoute = firstDest.loadActions
              .filter((a) => a.destinationActionTypeId === DestinationActionTypeId.Pickup)
              .map((a) => a.loadId)
            
            const newFirstDestId = getNextTempId()
            // Create a new first destination at the new waypoint location with Pickup actions (Bug fix: auto-generate Pickup on first destination)
            const newFirstDest: RouteDestination = {
              id: newFirstDestId,
              routeId: route.id,
              locationId,
              locationName,
              address,
              scheduledDate: firstDest.scheduledDate,
              sequence: 1,
              isUserCreated: true,
              isBoundary: true,
              loadActions: loadIdsInRoute.map((loadId) => ({
                id: getNextTempId(),
                routeDestinationId: newFirstDestId,
                loadId,
                destinationActionTypeId: DestinationActionTypeId.Pickup,
              })),
              loadServices: [],
            }
            
            // Resequence remaining destinations
            const remainingDests = route.destinations.slice(1).map((dest, idx) => ({
              ...dest,
              sequence: idx + 2,
            }))
            
            return {
              ...route,
              destinations: [newFirstDest, ...remainingDests],
            }
          }),
        }

        // Create the new leg with a SINGLE route that consolidates all loads from all routes
        // Collect all unique load IDs from all routes' first destinations (pickup actions)
        const allLoadIdsBefore = new Set<number>()
        const allPickupServices: RouteDestinationLoadService[] = []
        let firstDestForSchedule = referenceLeg.routes[0]?.destinations[0]
        let firstDestLocation = {
          locationId: firstDestForSchedule?.locationId || 0,
          locationName: firstDestForSchedule?.locationName,
          address: firstDestForSchedule?.address,
        }
        
        referenceLeg.routes.forEach((route) => {
          const firstDest = route.destinations[0]
          if (firstDest) {
            firstDest.loadActions
              .filter((a) => a.destinationActionTypeId === DestinationActionTypeId.Pickup)
              .forEach((a) => {
                allLoadIdsBefore.add(a.loadId)
              })
            if (firstDest.loadServices) {
              allPickupServices.push(...firstDest.loadServices)
            }
            // Use the first route's first destination for location info (they should all be the same origin)
            if (route === referenceLeg.routes[0]) {
              firstDestLocation = {
                locationId: firstDest.locationId,
                locationName: firstDest.locationName,
                address: firstDest.address,
              }
              firstDestForSchedule = firstDest
            }
          }
        })
        
        const newRouteIdBefore = getNextTempId()
        const pickupDestIdBefore = getNextTempId()
        const dropoffDestIdBefore = getNextTempId()
        
        // Pickup at original origin location (with all the original load actions)
        const pickupDestBefore: RouteDestination = {
          id: pickupDestIdBefore,
          routeId: newRouteIdBefore,
          locationId: firstDestLocation.locationId,
          locationName: firstDestLocation.locationName,
          address: firstDestLocation.address,
          scheduledDate: firstDestForSchedule?.scheduledDate || new Date().toISOString(),
          sequence: 1,
          isUserCreated: true,
          isBoundary: true,
          loadActions: Array.from(allLoadIdsBefore).map((loadId) => ({
            id: getNextTempId(),
            routeDestinationId: pickupDestIdBefore,
            loadId,
            destinationActionTypeId: DestinationActionTypeId.Pickup,
          })),
          loadServices: allPickupServices,
        }
        
        // Dropoff at new waypoint location - needs Drop-off actions for all loads being transported
        const dropoffDestBefore: RouteDestination = {
          id: dropoffDestIdBefore,
          routeId: newRouteIdBefore,
          locationId,
          locationName,
          address,
          scheduledDate: firstDestForSchedule?.scheduledDate || new Date().toISOString(),
          sequence: 2,
          isUserCreated: true,
          isBoundary: true,
          loadActions: Array.from(allLoadIdsBefore).map((loadId) => ({
            id: getNextTempId(),
            routeDestinationId: dropoffDestIdBefore,
            loadId,
            destinationActionTypeId: DestinationActionTypeId.DropOff,
          })),
          loadServices: [],
        }
        
        newLeg = {
          id: newLegId,
          journeyId: state.id || 0,
          sequence: 0, // Will be resequenced
          transportModeId,
          fulfilmentRequired: true,
          routes: [
            {
              id: newRouteIdBefore,
              legId: newLegId,
              displayOrder: 1,
              destinations: [pickupDestBefore, dropoffDestBefore],
              loadIds: Array.from(allLoadIdsBefore),
            },
          ],
        }
      }

      let newLegs: Leg[]
      let newWaypoints: JourneyWaypoint[]

      if (position === 'before') {
        // Insert new leg before the specified leg, waypoint comes after the new leg
        newLegs = [
          ...state.legs.slice(0, legIndex),
          newLeg,
          updatedReferenceLeg,
          ...state.legs.slice(legIndex + 1),
        ]
        // Insert waypoint at the position between the new leg and the original leg
        newWaypoints = [
          ...state.waypoints.slice(0, legIndex),
          newWaypoint,
          ...state.waypoints.slice(legIndex),
        ]
      } else {
        // Insert new leg after the specified leg, waypoint comes before the new leg
        newLegs = [
          ...state.legs.slice(0, legIndex),
          updatedReferenceLeg,
          newLeg,
          ...state.legs.slice(legIndex + 1),
        ]
        // Insert waypoint at the position between the original leg and the new leg
        newWaypoints = [
          ...state.waypoints.slice(0, legIndex + 1),
          newWaypoint,
          ...state.waypoints.slice(legIndex + 1),
        ]
      }

      // Resequence legs
      const resequencedLegs = newLegs.map((leg, index) => ({
        ...leg,
        sequence: index + 1,
      }))

      // Resequence waypoints
      const resequencedWaypoints = newWaypoints.map((wp, index) => ({
        ...wp,
        sequence: index + 1,
      }))

      return {
        ...state,
        legs: resequencedLegs,
        waypoints: resequencedWaypoints,
        isDirty: true,
      }
    }

    case 'DELETE_WAYPOINT_WITH_LEG': {
      const { waypointIndex, legToRemove } = action

      // Get the waypoint being deleted to determine which leg to remove
      const waypointToDelete = state.waypoints[waypointIndex]
      if (!waypointToDelete || waypointToDelete.afterLegIndex === undefined) {
        return state
      }

      // Determine which leg to remove based on the waypoint's afterLegIndex
      // The waypoint is between leg N (afterLegIndex) and leg N+1
      // - 'before' means remove leg N (the leg before the waypoint)
      // - 'after' means remove leg N+1 (the leg after the waypoint)
      const legIndexToRemove =
        legToRemove === 'before' ? waypointToDelete.afterLegIndex : waypointToDelete.afterLegIndex + 1

      // Ensure we don't remove a leg that doesn't exist
      if (legIndexToRemove < 0 || legIndexToRemove >= state.legs.length) {
        return state
      }

      // Get the leg being removed and the leg that will remain
      const legBeingRemoved = state.legs[legIndexToRemove]
      const remainingLegIndex = legToRemove === 'before' ? legIndexToRemove + 1 : legIndexToRemove - 1

      // Remove the waypoint
      const newWaypoints = state.waypoints.filter((_, index) => index !== waypointIndex)

      // Remove the leg
      const newLegs = state.legs.filter((_, index) => index !== legIndexToRemove)

      // Update the remaining leg to preserve the journey by merging route destinations
      // When removing the leg before the waypoint, the remaining leg (after) should keep its pickup
      // but update its first destination to match the removed leg's first destination
      // When removing the leg after the waypoint, the remaining leg (before) should keep its dropoff
      // but the journey continues from where the removed leg would have gone
      const updatedLegs = newLegs.map((leg, index) => {
        if (index === (legToRemove === 'before' ? remainingLegIndex - 1 : remainingLegIndex)) {
          // This is the remaining adjacent leg - update its routes to preserve journey continuity
          const routes = leg.routes.map((route, routeIndex) => {
            if (routeIndex === 0 && legToRemove === 'before' && legBeingRemoved.routes[0]) {
              // When removing leg before, update the remaining leg's first destination
              // to be the removed leg's first destination (the journey start point)
              const removedLegFirstDest = legBeingRemoved.routes[0].destinations[0]
              if (removedLegFirstDest && route.destinations.length > 0) {
                const updatedDestinations = [...route.destinations]
                updatedDestinations[0] = {
                  ...updatedDestinations[0],
                  locationId: removedLegFirstDest.locationId,
                  locationName: removedLegFirstDest.locationName,
                  address: removedLegFirstDest.address,
                }
                return { ...route, destinations: updatedDestinations }
              }
            } else if (routeIndex === leg.routes.length - 1 && legToRemove === 'after' && legBeingRemoved.routes[0]) {
              // When removing leg after, update the remaining leg's last destination
              // to be the removed leg's last destination (the journey end point)
              const removedLegDests = legBeingRemoved.routes[legBeingRemoved.routes.length - 1].destinations
              const removedLegLastDest = removedLegDests[removedLegDests.length - 1]
              if (removedLegLastDest && route.destinations.length > 0) {
                const updatedDestinations = [...route.destinations]
                updatedDestinations[updatedDestinations.length - 1] = {
                  ...updatedDestinations[updatedDestinations.length - 1],
                  locationId: removedLegLastDest.locationId,
                  locationName: removedLegLastDest.locationName,
                  address: removedLegLastDest.address,
                }
                return { ...route, destinations: updatedDestinations }
              }
            }
            return route
          })
          return { ...leg, routes }
        }
        return leg
      })

      // Resequence legs
      const resequencedLegs = updatedLegs.map((leg, index) => ({
        ...leg,
        sequence: index + 1,
      }))

      // Update afterLegIndex for remaining waypoints and resequence
      const resequencedWaypoints = newWaypoints.map((wp, index) => {
        let newAfterLegIndex = wp.afterLegIndex
        if (newAfterLegIndex !== undefined) {
          // If the waypoint was after the removed leg, decrement its afterLegIndex
          if (newAfterLegIndex > legIndexToRemove) {
            newAfterLegIndex = newAfterLegIndex - 1
          } else if (newAfterLegIndex === legIndexToRemove) {
            // If the waypoint was after the removed leg itself, it now comes after the previous leg
            newAfterLegIndex = Math.max(0, newAfterLegIndex - 1)
          }
        }
        return {
          ...wp,
          sequence: index + 1,
          afterLegIndex: newAfterLegIndex,
        }
      })

      return {
        ...state,
        legs: resequencedLegs,
        waypoints: resequencedWaypoints,
        isDirty: true,
      }
    }

    case 'SPLIT_ROUTE_ONE_PER_CONSIGNMENT': {
      const { legIndex, routeIndex, selectedLoadIds, carrierId, carrierName, transitIdentifier, scheduledDepartureDate, scheduledArrivalDate } = action
      const leg = state.legs[legIndex]
      const originalRoute = leg.routes[routeIndex]

      // Create a new route for each selected load
      const newRoutes: Route[] = selectedLoadIds.map((loadId, index) => {
        const newRouteId = getNextTempId()
        // Copy destinations from original route but only include actions for this load
        const newDestinations: RouteDestination[] = originalRoute.destinations.map((dest, destIndex) => {
          const newDestId = getNextTempId()
          const filteredActions = dest.loadActions
            .filter((action) => action.loadId === loadId)
            .map((action) => ({
              ...action,
              id: getNextTempId(),
              routeDestinationId: newDestId,
            }))
          return {
            ...dest,
            id: newDestId,
            routeId: newRouteId,
            sequence: destIndex + 1,
            loadActions: filteredActions,
          }
        })

        return {
          id: newRouteId,
          legId: leg.id,
          displayOrder: originalRoute.displayOrder + index + 1,
          carrierId,
          carrierName,
          transitIdentifier,
          scheduledDepartureDate,
          scheduledArrivalDate,
          destinations: newDestinations,
          loadIds: [loadId],
        }
      })

      // Update original route to remove the selected loads from its destinations
      const updatedOriginalDestinations: RouteDestination[] = originalRoute.destinations.map((dest) => ({
        ...dest,
                loadActions: dest.loadActions.filter(
                  (action) => !selectedLoadIds.includes(action.loadId)
                ),
      }))

      const updatedOriginalRoute: Route = {
        ...originalRoute,
        destinations: updatedOriginalDestinations,
        loadIds: originalRoute.loadIds.filter((id) => !selectedLoadIds.includes(id)),
      }

      // Combine original route (with remaining loads) and new routes
      const allRoutes = [
        ...leg.routes.slice(0, routeIndex),
        updatedOriginalRoute,
        ...newRoutes,
        ...leg.routes.slice(routeIndex + 1),
      ]

      // Resequence display orders
      const resequencedRoutes = allRoutes.map((route, index) => ({
        ...route,
        displayOrder: index + 1,
      }))

      const newLegs = [...state.legs]
      newLegs[legIndex] = {
        ...leg,
        routes: resequencedRoutes,
      }

      return {
        ...state,
        legs: newLegs,
        isDirty: true,
      }
    }

    case 'SPLIT_ROUTE_ALL_CONSIGNMENTS': {
      const { legIndex, routeIndex, selectedLoadIds, carrierId, carrierName, transitIdentifier, scheduledDepartureDate, scheduledArrivalDate } = action
      const leg = state.legs[legIndex]
      const originalRoute = leg.routes[routeIndex]

      // Create a single new route for all selected loads
      const newRouteId = getNextTempId()
      const newDestinations: RouteDestination[] = originalRoute.destinations.map((dest, destIndex) => {
        const newDestId = getNextTempId()
        const filteredActions = dest.loadActions
          .filter((action) => selectedLoadIds.includes(action.loadId))
          .map((action) => ({
            ...action,
            id: getNextTempId(),
            routeDestinationId: newDestId,
          }))
        return {
          ...dest,
          id: newDestId,
          routeId: newRouteId,
          sequence: destIndex + 1,
          loadActions: filteredActions,
        }
      })

      const newRoute: Route = {
        id: newRouteId,
        legId: leg.id,
        displayOrder: originalRoute.displayOrder + 1,
        carrierId,
        carrierName,
        transitIdentifier,
        scheduledDepartureDate,
        scheduledArrivalDate,
        destinations: newDestinations,
        loadIds: [...selectedLoadIds],
      }

      // Update original route to remove the selected loads from its destinations
      const updatedOriginalDestinations: RouteDestination[] = originalRoute.destinations.map((dest) => ({
        ...dest,
                loadActions: dest.loadActions.filter(
                  (action) => !selectedLoadIds.includes(action.loadId)
                ),
      }))

      const updatedOriginalRoute: Route = {
        ...originalRoute,
        destinations: updatedOriginalDestinations,
        loadIds: originalRoute.loadIds.filter((id) => !selectedLoadIds.includes(id)),
      }

      // Combine original route (with remaining loads) and the new route
      const allRoutes = [
        ...leg.routes.slice(0, routeIndex),
        updatedOriginalRoute,
        newRoute,
        ...leg.routes.slice(routeIndex + 1),
      ]

      // Resequence display orders
      const resequencedRoutes = allRoutes.map((route, index) => ({
        ...route,
        displayOrder: index + 1,
      }))

      const newLegs = [...state.legs]
      newLegs[legIndex] = {
        ...leg,
        routes: resequencedRoutes,
      }

      return {
        ...state,
        legs: newLegs,
        isDirty: true,
      }
    }

    case 'MERGE_ROUTES': {
      const {
        legIndex,
        selectedRouteIndices,
        carrierId,
        carrierName,
        transitIdentifier,
        scheduledDepartureDate,
        scheduledArrivalDate,
      } = action

      // Need at least 2 routes to merge
      if (selectedRouteIndices.length < 2) {
        return state
      }

      const leg = state.legs[legIndex]
      const sortedIndices = [...selectedRouteIndices].sort((a, b) => a - b)

      // Get all selected routes
      const selectedRoutes = sortedIndices.map((idx) => leg.routes[idx])

      // Create merged route with new ID
      const mergedRouteId = getNextTempId()

      // Collect all destinations from selected routes (preserving all locations)
      const allDestinations: {
        locationId: number
        locationName?: string
        address?: string
        scheduledDate: string
        isUserCreated: boolean
        isBoundary: boolean
        loadActions: RouteDestinationLoadAction[]
        loadServices: RouteDestinationLoadService[]
      }[] = []

      selectedRoutes.forEach((route) => {
        route.destinations.forEach((dest) => {
          allDestinations.push({
            locationId: dest.locationId,
            locationName: dest.locationName,
            address: dest.address,
            scheduledDate: dest.scheduledDate,
            isUserCreated: dest.isUserCreated,
            isBoundary: dest.isBoundary,
            loadActions: [...dest.loadActions],
            loadServices: [...dest.loadServices],
          })
        })
      })

      // Sort destinations by scheduledDate (earliest to latest)
      allDestinations.sort((a, b) => {
        const dateA = new Date(a.scheduledDate).getTime()
        const dateB = new Date(b.scheduledDate).getTime()
        return dateA - dateB
      })

      // Create merged destinations with new IDs and sequential sequence numbers
      const mergedDestinations: RouteDestination[] = allDestinations.map((destData, index) => {
        const newDestId = getNextTempId()
        return {
          id: newDestId,
          routeId: mergedRouteId,
          locationId: destData.locationId,
          locationName: destData.locationName,
          address: destData.address,
          scheduledDate: destData.scheduledDate,
          sequence: index + 1,
          isUserCreated: destData.isUserCreated,
          isBoundary: destData.isBoundary,
          loadActions: destData.loadActions.map((action) => ({
            ...action,
            id: getNextTempId(),
            routeDestinationId: newDestId,
          })),
          loadServices: destData.loadServices?.map((service) => ({
            ...service,
            id: getNextTempId(),
            routeDestinationId: newDestId,
          })) || [],
        }
      })

      // Create the merged route - combine loadIds from all selected routes
      const mergedLoadIds = [...new Set(selectedRoutes.flatMap((r) => r.loadIds))]
      const mergedRoute: Route = {
        id: mergedRouteId,
        legId: leg.id,
        displayOrder: selectedRoutes[0].displayOrder,
        carrierId,
        carrierName,
        transitIdentifier,
        scheduledDepartureDate,
        scheduledArrivalDate,
        destinations: mergedDestinations,
        loadIds: mergedLoadIds,
      }

      // Remove selected routes and add merged route
      const remainingRoutes = leg.routes.filter((_, idx) => !sortedIndices.includes(idx))
      const allRoutes = [mergedRoute, ...remainingRoutes]

      // Resequence display orders
      const resequencedRoutes = allRoutes.map((route, idx) => ({
        ...route,
        displayOrder: idx + 1,
      }))

      const newLegs = [...state.legs]
      newLegs[legIndex] = {
        ...leg,
        routes: resequencedRoutes,
      }

      return {
        ...state,
        legs: newLegs,
        isDirty: true,
      }
    }

    case 'INSERT_DESTINATION_WITH_ACTIONS': {
      const {
        legIndex,
        routeIndex,
        insertAfterDestIndex,
        locationId,
        locationName,
        address,
        scheduledDate,
        loadActions,
        loadServices,
      } = action

      const newLegs = [...state.legs]
      const leg = newLegs[legIndex]
      if (!leg || !leg.routes[routeIndex]) {
        return state
      }

      const route = leg.routes[routeIndex]
      const newRoutes = [...leg.routes]

      // Create load actions for the new destination
      const newDestId = getNextTempId()
      const newLoadActions: RouteDestinationLoadAction[] = loadActions.map((la) => ({
        id: getNextTempId(),
        routeDestinationId: newDestId,
        loadId: la.loadId,
        destinationActionTypeId: la.destinationActionTypeId,
      }))

      // Create load services for the new destination
      const newLoadServices: RouteDestinationLoadService[] = loadServices.map((ls) => ({
        id: getNextTempId(),
        routeDestinationId: newDestId,
        loadId: ls.loadId,
        serviceId: ls.serviceId,
      }))

      // Create the new destination
      const newDest: RouteDestination = {
        id: newDestId,
        routeId: route.id,
        locationId,
        locationName,
        address,
        scheduledDate,
        sequence: 0, // Will be resequenced
        isUserCreated: true,
        isBoundary: false,
        loadActions: newLoadActions,
        loadServices: newLoadServices,
      }

      // Insert the destination after the specified index
      const insertIndex = insertAfterDestIndex + 1
      const newDestinations = [
        ...route.destinations.slice(0, insertIndex),
        newDest,
        ...route.destinations.slice(insertIndex),
      ]

      // Resequence all destinations
      const resequencedDestinations = newDestinations.map((dest, idx) => ({
        ...dest,
        sequence: idx + 1,
      }))

      newRoutes[routeIndex] = {
        ...route,
        destinations: resequencedDestinations,
      }

      newLegs[legIndex] = {
        ...leg,
        routes: newRoutes,
      }

      return {
        ...state,
        legs: newLegs,
        isDirty: true,
      }
    }

    case 'UPDATE_LOAD_ACTIONS_AT_DESTINATION': {
      const { legIndex, routeIndex, destIndex, updatedActions, removedLoadIds } = action
      const newLegs = [...state.legs]
      const leg = newLegs[legIndex]
      if (!leg || !leg.routes[routeIndex]) return state

      const newRoutes = [...leg.routes]
      const route = newRoutes[routeIndex]
      if (!route.destinations[destIndex]) return state

      const newDestinations = [...route.destinations]
      const dest = newDestinations[destIndex]

      const updatedLoadActions: RouteDestinationLoadAction[] = updatedActions.map((config) => {
        const existingAction = dest.loadActions.find((a) => a.loadId === config.loadId)
        return {
          id: existingAction?.id || getNextTempId(),
          routeDestinationId: dest.id,
          loadId: config.loadId,
          destinationActionTypeId: config.destinationActionTypeId,
          notifyPartyId: existingAction?.notifyPartyId,
          completedDate: existingAction?.completedDate,
          clearanceRequired: config.clearanceRequired,
        }
      })

      const filteredLoadActions = updatedLoadActions.filter(
        (action) => !removedLoadIds.includes(action.loadId)
      )

      // Build loadServices from the serviceIds in updatedActions
      const newLoadServices: RouteDestinationLoadService[] = []
      updatedActions.forEach((config) => {
        if (removedLoadIds.includes(config.loadId)) return
        config.serviceIds.forEach((serviceId) => {
          // Check if this service already exists for this load
          const existingService = dest.loadServices.find(
            (s) => s.loadId === config.loadId && s.serviceId === serviceId
          )
          newLoadServices.push({
            id: existingService?.id || getNextTempId(),
            routeDestinationId: dest.id,
            loadId: config.loadId,
            serviceId,
          })
        })
      })

      newDestinations[destIndex] = {
        ...dest,
        loadActions: filteredLoadActions,
        loadServices: newLoadServices,
      }

      newRoutes[routeIndex] = {
        ...route,
        destinations: newDestinations,
      }

      newLegs[legIndex] = {
        ...leg,
        routes: newRoutes,
      }

      return {
        ...state,
        legs: newLegs,
        isDirty: true,
      }
    }

    default:
      return state
  }
}
